import type {
  MenuListProps as MuiMenuListProps,
  MenuProps as MuiMenuProps,
  PopperProps as MuiPopperProps,
} from '@mui/material';
import { Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import useStyle from './hooks/useStyle';

export interface MenuOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

export interface PopperMenuProps extends Omit<MuiPopperProps, 'children' | 'onSelect'> {
  options: MenuOption[];
  open: boolean;
  selectedValue?: string | number;
  menuItemStyles?: React.CSSProperties;
  selectedItemStyles?: React.CSSProperties;
  minWidth?: number;
  customMenuStyles?: React.CSSProperties;
  popperProps?: MuiPopperProps;
  menuProps?: MuiMenuProps;
  menuListProps?: MuiMenuListProps;
  onSelect: (value: string | number, option: MenuOption) => void;
  onClose: (e: MouseEvent | TouchEvent) => void;
}
export default function PopperMenu({
  options,
  selectedValue,
  menuItemStyles = {},
  selectedItemStyles = {},
  minWidth = 180,
  customMenuStyles = {},
  open,
  placement,
  popperProps,
  menuListProps,
  anchorEl,
  onSelect,
  onClose,
}: PopperMenuProps) {
  const { defaultMenuStyles, defaultMenuItemStyles, defaultSelectedItemStyles } = useStyle({
    minWidth,
    customMenuStyles,
    menuItemStyles,
    selectedItemStyles,
  });
  const handleSelect = (option: MenuOption) => {
    if (option.disabled) return;
    onSelect?.(option.value, option);
  };
  return (
    <Popper
      open={open}
      role={undefined}
      placement={placement}
      anchorEl={anchorEl}
      transition
      disablePortal
      {...popperProps}
      sx={defaultMenuStyles}
    >
      {({ TransitionProps, placement: popperPlacement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin: popperPlacement === 'bottom-start' ? 'left top' : 'left bottom',
          }}
        >
          <Paper>
            <ClickAwayListener onClickAway={onClose}>
              <MenuList
                autoFocusItem={open}
                id="popper-menu"
                aria-labelledby="popper-menu"
                {...menuListProps}
              >
                {options.map((option) => {
                  const isSelected = option.value === selectedValue;
                  return (
                    <MenuItem
                      key={option.value}
                      onClick={() => handleSelect(option)}
                      selected={isSelected}
                      disabled={option.disabled}
                      sx={{
                        ...defaultMenuItemStyles,
                        ...(isSelected && {
                          ...defaultSelectedItemStyles,
                          '&.Mui-selected': defaultSelectedItemStyles,
                          '&.Mui-selected:hover': defaultSelectedItemStyles['&:hover'],
                        }),
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  );
                })}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
}
